import {
  FleetAssetCapabilityRequestDTO,
  useAddCapabilitiesToVehicleMutation,
  useCreateVehicleMutation,
  useDeleteVehicleMutation,
  useRemoveCapabilitiesFromVehicleMutation,
  useUpdateVehicleMutation,
  Vehicle,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {ResultTypeFrom} from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";
import {EntityMutationCallback} from "./useEntityMutation";

export type UseVehicleMutationProps = {}

export type UseVehicleMutationResult = {
  createVehicle: (vehicle: Partial<Vehicle>, callback: EntityMutationCallback<Vehicle> | undefined) => void
  updateVehicle: (vehicle: Partial<Vehicle>, callback: EntityMutationCallback<Vehicle> | undefined) => void
  deleteVehicle: (vehicleId: string, callback: EntityMutationCallback | undefined) => void
  addCapabilities: (vehicleId: string, data: FleetAssetCapabilityRequestDTO, callback: EntityMutationCallback | undefined) => void
  removeCapabilities: (vehicleId: string, data: FleetAssetCapabilityRequestDTO, callback: EntityMutationCallback | undefined) => void

  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
  isAddingCapabilities?: boolean
  isRemovingCapabilities?: boolean
}

export const useVehicleMutation = (props: UseVehicleMutationProps = {}): UseVehicleMutationResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)
  const [_addingCapabilities, _setAddingCapabilities] = useDebouncedState(false, 500)
  const [_removingCapabilities, _setRemovingCapabilities] = useDebouncedState(false, 500)

  // possible mutations
  const [_createVehicle, {isLoading: isCreating}] = useCreateVehicleMutation()
  const [_updateVehicle, {isLoading: isUpdating}] = useUpdateVehicleMutation()
  const [_deleteVehicle, {isLoading: isDeleting}] = useDeleteVehicleMutation()
  const [_addCapabilities, {isLoading: isAddingCapabilities}] = useAddCapabilitiesToVehicleMutation()
  const [_removeCapabilities, {isLoading: isRemovingCapabilities}] = useRemoveCapabilitiesFromVehicleMutation()

  useEffect(() => {
    _setCreating(isCreating)
  }, [isCreating, _setCreating])

  useEffect(() => {
    _setUpdating(isUpdating)
  }, [isUpdating, _setUpdating])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    _setAddingCapabilities(isAddingCapabilities)
  }, [isAddingCapabilities, _setAddingCapabilities])

  useEffect(() => {
    _setRemovingCapabilities(isRemovingCapabilities)
  }, [isRemovingCapabilities, _setRemovingCapabilities])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "useVehicleUpdate_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entity.vehicle", "Vehicle"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useVehicleUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating || _addingCapabilities || _removingCapabilities) {
      notifications.show({
        id: "useVehicleUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.vehicle", "Vehicle"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useVehicleUpdate_updating")
    }
  }, [_updating, _addingCapabilities, _removingCapabilities, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useVehicleUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.vehicle", "Vehicle"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useVehicleUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleCreateResult = useCallback((promise: Promise<Vehicle>, callback: EntityMutationCallback<Vehicle> | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.vehicle", "Vehicle") + " " + t("common.status.created", "Created"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(payload)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleUpdateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, callback: EntityMutationCallback<Vehicle> | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.vehicle", "Vehicle") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(undefined)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })
          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, callback: EntityMutationCallback | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.vehicle", "Vehicle") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (callback?.onSuccess) {
          callback.onSuccess(undefined)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteConfirm = useCallback((vehicleId: string, callback: EntityMutationCallback | undefined) => {
    const promise = _deleteVehicle({id: vehicleId}).unwrap()
    handleDeleteResult(promise, callback)
  }, [_deleteVehicle, handleDeleteResult])

  const createVehicle = useCallback((vehicle: Partial<Vehicle>, callback: EntityMutationCallback<Vehicle> | undefined) => {
    const promise = _createVehicle(vehicle).unwrap()
    handleCreateResult(promise, callback)
  }, [_createVehicle, handleCreateResult])

  const updateVehicle = useCallback((vehicle: Partial<Vehicle>, callback: EntityMutationCallback | undefined) => {
    const promise = _updateVehicle(vehicle).unwrap()
    handleUpdateResult(promise, callback)
  }, [_updateVehicle, handleUpdateResult])

  const deleteVehicle = useCallback((vehicleId: string, callback: EntityMutationCallback | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.vehicle", "Vehicle"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.deletevehicle.confirm", "Are you sure you want to delete this vehicle?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(vehicleId, callback)
      },
    })
  }, [handleDeleteConfirm, t])

  const addCapabilities = useCallback((vehicleId: string, data: FleetAssetCapabilityRequestDTO, callback: EntityMutationCallback | undefined) => {
    const promise = _addCapabilities({vehicleId, data}).unwrap()
    handleUpdateResult(promise, callback)
  }, [_addCapabilities, handleUpdateResult])

  const removeCapabilities = useCallback((vehicleId: string, data: FleetAssetCapabilityRequestDTO, callback: EntityMutationCallback | undefined) => {
    const promise = _removeCapabilities({vehicleId, data}).unwrap()
    handleUpdateResult(promise, callback)
  }, [_removeCapabilities, handleUpdateResult])

  return {
    createVehicle: createVehicle,
    updateVehicle: updateVehicle,
    deleteVehicle: deleteVehicle,
    addCapabilities: addCapabilities,
    removeCapabilities: removeCapabilities,
    isCreating: _creating,
    isUpdating: _updating,
    isDeleting: _deleting,
    isAddingCapabilities: _addingCapabilities,
    isRemovingCapabilities: _removingCapabilities,
  }
}
